import React, { FC, useMemo } from "react";
import { MultiSelect } from "@src/lib/ui/MultiSelect";
import { useGetRolesWithEmployeesQuery } from "@src/features/users/ui/operations.generated";
import { pluralize } from "@src/lib/utils/strings";
import IdBadge2OutlineIcon from "@src/lib/icons/18px/id-badge-2-outline";

type Props = {
  roleIds: number[];
  setRoleIds: (roleIds: number[]) => void;
  showRolesWithNoTrainees?: boolean;
  placeholder?: string;
};

const RoleMultiSelect: FC<Props> = ({
  roleIds,
  setRoleIds,
  showRolesWithNoTrainees = false,
  placeholder = "Search roles...",
}) => {
  const { data } = useGetRolesWithEmployeesQuery();

  const allRoles = useMemo(() => data?.Roles.objects || [], [data]);

  const availableRoles = useMemo(() => {
    let filteredRoles = allRoles;
    if (!showRolesWithNoTrainees) {
      filteredRoles = filteredRoles.filter(
        (role) => role.employees && role.employees.length > 0,
      );
    }
    return filteredRoles;
  }, [allRoles, showRolesWithNoTrainees]);

  const options = useMemo(
    () =>
      availableRoles.map((role) => ({
        label: role.name,
        value: role.id.toString(),
        icon: IdBadge2OutlineIcon,
        subtitle: role.employees
          ? `${role.employees.length} ${pluralize(
              "Trainee",
              role.employees.length,
            )}`
          : undefined,
      })),
    [availableRoles],
  );

  const handleValueChange = (selectedValues: string[]) => {
    const selectedRoles = allRoles.filter((role) =>
      selectedValues.includes(role.id.toString()),
    );
    setRoleIds(selectedRoles.map((x) => x.id));
  };

  return (
    <MultiSelect
      options={options}
      onValueChange={handleValueChange}
      defaultValue={roleIds.map((id) => id.toString())}
      placeholder={placeholder}
      dialogPopover={true}
    />
  );
};

export default RoleMultiSelect;
